import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { FiCheckCircle, FiChevronRight } from "react-icons/fi";
import orzelLogo from "../../img/orzel_logo_192.png";
import { RenderMarkdownWithLatex } from "../../utils/utils";
import SolutionStepper from "./SolutionStepper";
import BackupModeWidget from "./BackupModeWidget";
import { getRevealProps } from "../../utils/pageTransition";

// Ten sam język wizualny co siatka odpowiedzi (granat → zielony/czerwony + ring).
const answerFeedbackVariants = {
    idle: { scale: 1, x: 0 },
    correct: { scale: [1, 1.06, 1], transition: { duration: 0.4, ease: "easeOut", times: [0, 0.4, 1] } },
    incorrect: { x: [0, -8, 8, -6, 6, -3, 0], transition: { duration: 0.4, ease: "easeInOut" } }
};

const openBtnClass = (state) => {
    let c = "flex items-center justify-center text-center rounded-lg text-xs font-apotek tracking-wide text-slate-100 px-3 py-2 shadow-md transition-colors duration-200 disabled:cursor-not-allowed w-full";
    if (state === 'green') c += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
    else if (state === 'red') c += " bg-[#8A0000] opacity-90";
    else if (state === 'faded') c += " bg-[#233855] opacity-40";
    else c += " bg-[#233855] hover:bg-[#345081]";
    return c;
};

const DecisionButton = ({ state, onClick, disabled, reduced, children }) => (
    <motion.button
        onClick={onClick}
        disabled={disabled}
        className={openBtnClass(state)}
        variants={answerFeedbackVariants}
        animate={reduced ? 'idle' : state === 'green' ? 'correct' : state === 'red' ? 'incorrect' : 'idle'}
        whileTap={!disabled ? { scale: 0.96 } : undefined}
    >
        {children}
    </motion.button>
);

const AskEagleButton = ({ onClick, disabled }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
    >
        <div className="w-9 h-9 rounded-full bg-[#233855] flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src={orzelLogo} alt="Orzeł AI" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 text-left min-w-0">
            <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Zapytaj Orła AI</span>
            <span className="block text-xs text-gray-400 leading-tight">Wyjaśni krok po kroku</span>
        </div>
        <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
            <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
        </div>
    </button>
);

/** Sekcja WEWNĄTRZ białej karty: "Sprawdź odpowiedź" → poprawna odpowiedź → decyzja 1. */
export const OpenProblemDecision = ({
    currentProblem, flow, isAnswerChecked, wasLastAnswerCorrect, showSolution, setShowSolution,
}) => {
    const reduced = useReducedMotion();
    const { openRevealed, setOpenRevealed, openLadder, openPick, pickGood, pickBad1 } = flow;

    return (
        <div className="space-y-3">
            {!openRevealed && (
                <button
                    onClick={() => setOpenRevealed(true)}
                    className="flex w-full justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] transition-colors active:scale-[0.98]"
                >
                    SPRAWDŹ ODPOWIEDŹ
                </button>
            )}

            {openRevealed && (
                <>
                    <div className="w-full rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="bg-[#233855] px-4 py-1 flex items-center gap-2">
                            <FiCheckCircle className="text-white w-3 h-3" />
                            <span className="text-xs font-bold font-apotek uppercase tracking-widest text-white">Poprawna odpowiedź</span>
                        </div>
                        <div className="p-4 text-center text-base text-gray-900 overflow-x-auto">
                            <RenderMarkdownWithLatex text={currentProblem.true_answer} />
                        </div>
                    </div>

                    {/* DECYZJA 1 */}
                    <div className="grid grid-cols-2 gap-2">
                        <DecisionButton
                            state={openLadder ? 'red' : (openPick === 'good1' ? 'faded' : 'default')}
                            onClick={pickBad1}
                            disabled={!!openPick}
                            reduced={reduced}
                        >
                            MAM INACZEJ
                        </DecisionButton>
                        <DecisionButton
                            state={openPick === 'good1' ? 'green' : (openLadder ? 'faded' : 'default')}
                            onClick={() => pickGood('good1')}
                            disabled={!!openPick}
                            reduced={reduced}
                        >
                            MAM DOBRZE
                        </DecisionButton>
                    </div>
                </>
            )}

            {isAnswerChecked && wasLastAnswerCorrect && !showSolution && !openLadder && (
                <button
                    onClick={() => setShowSolution(true)}
                    className="w-full text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-colors"
                >
                    ROZWIĄZANIE
                </button>
            )}
        </div>
    );
};

/** Sekcja POZA kartą (po "Mam inaczej"): pełne rozwiązanie + decyzja finalna. */
export const OpenProblemLadder = ({
    currentProblem, flow, isAnswerChecked, isBackupModeTriggered,
    onOpenChat, isResponding, showBackupWidget = true,
}) => {
    const reduced = useReducedMotion();
    const { openPick, pickGood, pickBad2 } = flow;

    return (
        <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
            <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
            <AskEagleButton onClick={onOpenChat} disabled={isResponding} />
            <div className="grid grid-cols-2 gap-2">
                <DecisionButton
                    state={openPick === 'bad2' ? 'red' : (isAnswerChecked ? 'faded' : 'default')}
                    onClick={pickBad2}
                    disabled={isAnswerChecked}
                    reduced={reduced}
                >
                    POWAŻNE BŁĘDY
                </DecisionButton>
                <DecisionButton
                    state={openPick === 'good2' ? 'green' : (isAnswerChecked ? 'faded' : 'default')}
                    onClick={() => pickGood('good2')}
                    disabled={isAnswerChecked}
                    reduced={reduced}
                >
                    DROBNY BŁĄD
                </DecisionButton>
            </div>
            {showBackupWidget && (
                <AnimatePresence>
                    {isAnswerChecked && isBackupModeTriggered && (
                        <motion.div {...getRevealProps(reduced)}>
                            <BackupModeWidget variant="error" />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
};

export { AskEagleButton };
